import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const debounce = (fn, ms = 350) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

const StageAddItemModal = ({ apiUrl, stageId, onClose, onSaved }) => {
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [deviceOptions, setDeviceOptions] = useState([]); // unique devices
  const [resultsCache, setResultsCache] = useState([]); // raw rows from server (includes locations/qty)

  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [fromLocationId, setFromLocationId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const searchRef = useRef(null);

  // debounced server search using your existing endpoint:
  // POST inventory_crud.php { status:'searchInventory', data:{ search } }
  const runSearch = useMemo(
    () =>
      debounce(async (term) => {
        if (!term || term.length < 2) {
          setDeviceOptions([]);
          setResultsCache([]);
          return;
        }
        setSearching(true);
        try {
          const body = { status: 'searchInventory', data: { search: term } };
          const res = await axios.post(`${apiUrl}/inventory_crud.php`, body, {
            headers: { 'Content-Type': 'application/json' },
          });

          if (Array.isArray(res.data) && res.data[0] === 'success') {
            const rows = res.data[1] || [];
            setResultsCache(rows);

            // Build unique device list (by device_id)
            const byId = new Map();
            rows.forEach((r) => {
              if (!byId.has(r.device_id)) {
                byId.set(r.device_id, {
                  value: r.device_id,
                  label: `${r.device} ${r.manufacture ? '– ' + r.manufacture : ''}`,
                  description: r.description || '',
                });
              }
            });
            setDeviceOptions(Array.from(byId.values()));
          } else {
            setDeviceOptions([]);
            setResultsCache([]);
          }
        } catch (e) {
          if (e?.response?.status === 401) {
            toast.error('Session expired. Please login again.', { position: 'top-center' });
          } else {
            toast.error('Error searching devices');
          }
          setDeviceOptions([]);
          setResultsCache([]);
        } finally {
          setSearching(false);
        }
      }, 350),
    [apiUrl]
  );

  useEffect(() => {
    runSearch(search);
  }, [search, runSearch]);

  // Locations for selected device, with qty
  const locationOptions = useMemo(() => {
    if (!selectedDeviceId) return [];
    const rows = resultsCache.filter((r) => Number(r.device_id) === Number(selectedDeviceId));
    // Expect fields: location_id, location, sub_location, current_inventory
    // Only show locations with positive stock (optional)
    const options = rows
      .filter((r) => Number(r.current_inventory) > 0)
      .map((r) => ({
        value: r.location_id,
        label: `${r.location}${r.sub_location ? ' ' + r.sub_location : ''} — Qty: ${r.current_inventory}`,
        qty: Number(r.current_inventory),
      }));
    // fallback: if all were filtered out, show all locations anyway
    return options.length ? options : rows.map((r) => ({
      value: r.location_id,
      label: `${r.location}${r.sub_location ? ' ' + r.sub_location : ''} — Qty: ${r.current_inventory}`,
      qty: Number(r.current_inventory),
    }));
  }, [resultsCache, selectedDeviceId]);

  const selectedDeviceMeta = useMemo(() => {
    return deviceOptions.find((d) => Number(d.value) === Number(selectedDeviceId)) || null;
  }, [deviceOptions, selectedDeviceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const deviceId = Number(selectedDeviceId);
    const fromLoc = Number(fromLocationId);
    const qty = Number(quantity);

    if (!deviceId) {
      toast.warn('Select a device');
      return;
    }
    if (!fromLoc) {
      toast.warn('Select a source location');
      return;
    }
    if (!qty || qty <= 0) {
      toast.warn('Quantity must be greater than 0');
      return;
    }

    // Optional check: prevent requesting more than available at that location
    const loc = locationOptions.find((o) => Number(o.value) === fromLoc);
    if (loc && loc.qty != null && qty > loc.qty) {
      toast.warn(`Only ${loc.qty} available at the selected location`);
      return;
    }

    const payload = {
      stage_id: Number(stageId),
      device_id: deviceId,
      quantity: qty,
      from_location_id: fromLoc,
      notes: notes?.trim() || null,
    };

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        { status: 'addStagingItem', data: payload },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const ok =
        (res.data && res.data.success === true) ||
        (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        toast.success('Item staged');
        onSaved?.();
      } else {
        toast.error(res.data?.error || 'Failed to add item');
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
      } else {
        toast.error(e?.response?.data?.error || 'API error adding item');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add Item to Stage</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Device search + select */}
          <div>
            <label className="block text-sm font-medium mb-1">Device</label>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search device name, model, or description…"
              className="w-full border rounded px-3 py-2"
            />
            <div className="mt-2 max-h-48 overflow-auto border rounded">
              {searching && <div className="p-2 text-sm text-gray-500">Searching…</div>}
              {!searching && deviceOptions.length === 0 && search.length >= 2 && (
                <div className="p-2 text-sm text-gray-400">No devices found</div>
              )}
              {!searching && deviceOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setSelectedDeviceId(opt.value);
                    setFromLocationId(''); // reset location
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${
                    Number(selectedDeviceId) === Number(opt.value) ? 'bg-indigo-50 font-medium' : ''
                  }`}
                >
                  <div>{opt.label}</div>
                  {opt.description && (
                    <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
                  )}
                </button>
              ))}
            </div>
            {selectedDeviceMeta && (
              <div className="text-xs text-gray-600 mt-2">
                Selected: <span className="font-semibold">{selectedDeviceMeta.label}</span>
              </div>
            )}
          </div>

          {/* From Location (built from selected device’s rows) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">From Location</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={fromLocationId}
                onChange={(e) => setFromLocationId(e.target.value)}
                disabled={!selectedDeviceId}
              >
                <option value="">Select location</option>
                {locationOptions.map((loc) => (
                  <option key={loc.value} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Quantity</label>
              <input
                type="number"
                min={1}
                className="w-full border rounded px-3 py-2"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={!selectedDeviceId}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              rows={3}
              className="w-full border rounded px-3 py-2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 rounded border" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded disabled:opacity-60"
              disabled={submitting || !selectedDeviceId || !fromLocationId}
            >
              {submitting ? 'Saving…' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StageAddItemModal;
